import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DprCardService } from '@dpr/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { dynamicTextPlaceholder } from '@shared/enums';
import {
  DprApprover,
  DprOnlineApproval,
  DprOnlineApprover,
} from '@shared/models/dpr';
import {
  Approver,
  OnlineApprover,
  SubprojectConfigurationCustomNames,
} from '@shared/models/project-setup';
import { DprModalTitlePipe } from '@shared/pipes';
import { ToastNotificationService } from '@shared/services';
import { Constants } from '@shared/utils';
import { BehaviorSubject, filter, Observable, take, tap } from 'rxjs';
import { DprOnlineApproveModalComponent } from '..';

@UntilDestroy()
@Component({
  selector: 'app-dpr-card-approvers',
  templateUrl: './dpr-card-approvers.component.html',
  styleUrls: ['./dpr-card-approvers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprCardApproversComponent implements OnChanges {
  @Input({ required: true }) dprApprovers$: Observable<DprApprover[]>;
  @Input({ required: true }) approvers: Approver[];
  @Input({ required: true }) onlineApprovers: OnlineApprover[];
  @Input({ required: true }) dprOnlineApprovals: DprOnlineApproval[];
  @Input({ required: true }) customNames: SubprojectConfigurationCustomNames;
  @Input({ required: true }) accountId: string;
  @Input({ required: true }) clientId: string;
  @Input({ required: true }) clientName: string;
  @Input({ required: true }) dprDetailId: number | string;
  @Input({ required: true }) dateTime: Date;
  @Input({ required: true }) displayId: number;
  @Input({ required: true }) revision: number;
  @Input({ required: true }) isLocked: boolean;
  @Input({ required: true }) canEdit: boolean;
  @Input({ required: true }) syncStatus$: Observable<boolean>;
  @Output() addDprApprover = new EventEmitter<DprApprover>();
  @Output() updateDprApprover = new EventEmitter<DprApprover>();
  @Output() deleteDprApprover = new EventEmitter<DprApprover>();
  @Output() syncOnlineApproval = new EventEmitter<DprOnlineApproval>();

  approverSaved$ = new BehaviorSubject<boolean>(false);
  addedDprApprovers = new Map<number, DprApprover>();
  dprOnlineApprovers: DprOnlineApprover[];

  constructor(
    private toastNotificationService: ToastNotificationService,
    private dialog: MatDialog,
    private dprCardService: DprCardService,
    private dprModalTitlePipe: DprModalTitlePipe
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes?.onlineApprovers?.currentValue ||
      changes?.dprOnlineApprovals?.currentValue
    ) {
      this.dprOnlineApprovers = this.onlineApprovers
        ?.map((a: OnlineApprover) => {
          return {
            accountId: a.accountId,
            userName: `${a.userName}${
              a.accountId === this.accountId
                ? ' ' + $localize`:@@dpr.dprCardApprovers.txtCurrentUser:(you)`
                : ''
            }`,
            isCurrentUser: a.accountId === this.accountId,
            canApprove: a.canApprove,
            dprOnlineApproval: this.dprOnlineApprovals?.find(
              (doa) => doa.accountId === a.accountId
            ),
          };
        })
        .filter((a) => !!a.canApprove || !!a.dprOnlineApproval);
      this.dialog.getDialogById('onlineApproveModal')?.close();
    }
  }

  openOnlineApproveModal(dprOnlineApprover: DprOnlineApprover): void {
    const dialogRef = this.dialog.open(DprOnlineApproveModalComponent, {
      ...Constants.DialogOptions,
      id: 'onlineApproveModal',
      data: {
        dprOnlineApprover,
        customNames: this.customNames,
        dprDetailId: this.dprDetailId,
        title: this.dprModalTitlePipe.transform(
          this.dprCardService.getModalTitle(
            this.displayId,
            this.dateTime,
            this.revision
          ),
          dprOnlineApprover.isCurrentUser
            ? $localize`:@@dpr.dprCardApprovers.txtUpdateStatus:Update status`
            : dprOnlineApprover?.userName
        ),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((result) => !!result),
        tap((dialogResult: DprOnlineApproval) => {
          this.syncOnlineApproval.emit(dialogResult);
        })
      )
      .subscribe();
  }

  addDprApproverItem(approverId: number): void {
    if (!this.addedDprApprovers.has(approverId)) {
      this.addedDprApprovers.set(approverId, {
        name: '',
        approverId,
        dprDetailId: this.dprDetailId,
      });
      this.addedDprApprovers = new Map(this.addedDprApprovers); // needed to trigger change detection
    }
  }

  removeDprApproverItem(approverId: number): void {
    this.addedDprApprovers.delete(approverId);
    this.addedDprApprovers = new Map(this.addedDprApprovers); // needed to trigger change detection
  }

  onSaveDprApprover(dprApprover: DprApprover): void {
    this.registerForSyncChanges(dprApprover);

    if (dprApprover.id) {
      this.updateDprApprover.emit(dprApprover);
    } else {
      this.addDprApprover.emit(dprApprover);
    }
  }

  onDeleteDprApprover(dprApprover: DprApprover): void {
    if (!dprApprover.id) {
      this.removeDprApproverItem(dprApprover.approverId);
    } else {
      this.registerForSyncChanges(dprApprover);
      this.deleteDprApprover.emit(dprApprover);
    }
  }

  trackApprover(_index: number, approver: Approver): number {
    return approver?.id;
  }

  trackDprApprover(_index: number, dprApprover: DprApprover): number | string {
    return dprApprover?.id;
  }

  trackOnlineApprover(_index: number, onlineApprover: OnlineApprover): string {
    return onlineApprover.accountId;
  }

  private registerForSyncChanges(dprApprover: DprApprover): void {
    this.syncStatus$
      .pipe(
        untilDestroyed(this),
        take(1),
        tap((syncResult) => {
          if (syncResult) {
            this.removeDprApproverItem(dprApprover.approverId);
            this.approverSaved$.next(true);
          } else {
            const errorMessage = $localize`:@@dpr.dprCardApprovers.txtErrorSavingApprover:Error saving changes on the DPR approver (---)`;
            this.toastNotificationService.error(
              errorMessage.replace(dynamicTextPlaceholder, dprApprover.name)
            );
          }
        })
      )
      .subscribe();
  }
}
